import apiClient from './apiClient';
import { db } from '../offline/db';
import { deriveKey, encryptToken } from '../offline/crypto';
import { getDeviceId } from './apiClient';

interface LoginRequest {
    email?: string;
    username?: string;
    password: string;
}

export interface ProfileAction {
    id: number;
    action_code: string;
    code?: string;
    is_active: boolean;
}

export interface ProfileModule {
    id: number;
    code: string;
    name: string;
    is_active: boolean;
    actions: ProfileAction[];
}

export interface UserProfile {
    id: number;
    code?: string;
    name: string;
    modules: ProfileModule[];
}

export interface UserRole {
    id: number;
    name: string;
}

export interface AuthDoctor {
    id: number;
    first_name: string;
    last_name: string;
    specialty: { id: number; name: string } | null;
}

export interface AuthUser {
    id: number;
    name: string;
    lastname?: string;
    email: string;
    username?: string | null;
    is_admin: boolean;
    status?: string;
    must_change_password?: boolean;
    patient_id?: number | null;
    profile: UserProfile | null;
    /** @deprecated Kept for backward compatibility. Use is_admin instead. */
    roles: UserRole[];
    doctor: AuthDoctor | null;
}

interface LoginResponse {
    user: AuthUser;
    token?: string;
    expires_at?: string;
}

interface ApiResponse<T> {
    success: boolean;
    data: T;
    message?: string;
}

// Token TTL in milliseconds (30 days)
const PWA_TOKEN_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export const authService = {
    async login(credentials: LoginRequest): Promise<LoginResponse> {
        const response = await apiClient.post<ApiResponse<LoginResponse>>('/login', credentials);
        const responseData = response.data.data;
        const { user, token, expires_at: expiresAt } = responseData;

        // Store the PWA token in IndexedDB if provided by the backend
        if (token) {
            await this.storePwaToken(token, user.id, expiresAt);
        }

        localStorage.setItem('user', JSON.stringify(user));
        return responseData;
    },

    /**
     * Stores an encrypted PWA token in IndexedDB.
     * The token is encrypted using AES-GCM with a key derived from userId + deviceId.
     */
    async storePwaToken(token: string, userId: number, expiresAt?: string): Promise<void> {
        try {
            const deviceId = await getDeviceId();
            const key = await deriveKey(userId, deviceId);
            const encrypted = await encryptToken(token, key);

            const expiresAtMs = expiresAt
                ? new Date(expiresAt).getTime()
                : Date.now() + PWA_TOKEN_TTL_MS;

            await db.auth_tokens.put({
                key: 'pwa_token',
                iv: encrypted.iv,
                ciphertext: encrypted.ciphertext,
                expires_at: expiresAtMs,
                user_id: userId,
            });
        } catch (error) {
            console.error('[authService] Failed to store PWA token:', error);
        }
    },

    async me(): Promise<AuthUser> {
        const response = await apiClient.get<ApiResponse<AuthUser>>('/me');
        const user = response.data.data;
        localStorage.setItem('user', JSON.stringify(user));
        return user;
    },

    async updateMe(payload: { name?: string; lastname?: string; email?: string }): Promise<AuthUser> {
        const response = await apiClient.put<ApiResponse<AuthUser>>('/me', payload);
        const user = response.data.data;
        localStorage.setItem('user', JSON.stringify(user));
        return user;
    },

    async logout(): Promise<void> {
        await apiClient.post('/logout');
        localStorage.removeItem('user');

        // Clear ALL IndexedDB offline stores — PHI hygiene (medical data must not persist after logout)
        try {
            await db.auth_tokens.clear();
            await db.appointments.clear();
            await db.pending_writes.clear();
            await db.sync_meta.clear();
        } catch (error) {
            console.error('[authService] Failed to clear offline stores:', error);
        }

        // Unregister the service worker — ensures no cached PHI is served to next user
        if ('serviceWorker' in navigator) {
            try {
                const registrations = await navigator.serviceWorker.getRegistrations();
                for (const reg of registrations) {
                    await reg.unregister();
                }
            } catch (error) {
                console.error('[authService] Failed to unregister service workers:', error);
            }
        }
    },

    async refreshToken(): Promise<{ token: string }> {
        const response = await apiClient.post<ApiResponse<{ token: string }>>('/refresh');
        return response.data.data;
    },

    async changePassword(currentPassword: string, newPassword: string, confirmPassword: string): Promise<AuthUser> {
        const response = await apiClient.post<ApiResponse<AuthUser>>('/change-password', {
            current_password: currentPassword,
            new_password: newPassword,
            new_password_confirmation: confirmPassword,
        });
        const user = response.data.data;
        localStorage.setItem('user', JSON.stringify(user));
        return user;
    },

    isAuthenticated(): boolean {
        return !!localStorage.getItem('user');
    },

    getUser(): AuthUser | null {
        try {
            const userStr = localStorage.getItem('user');
            return userStr ? JSON.parse(userStr) : null;
        } catch {
            localStorage.removeItem('user');
            return null;
        }
    },
};

export default authService;
